import axios from 'axios';


export interface TimingSlotConfig {
  slotType: 'four_hours' | 'eight_hours' | 'custom';
  ratePerFourHours: number;
  fullDayRate: number;
  description: string;
  availableStartTimes: string[];
  minBookingHours: number;
  maxBookingHours: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://bpphmdademo.alliantprojects.com/api/v1';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // For FormData, let browser set Content-Type with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  
  console.log('Venues API Request:', {
    method: config.method?.toUpperCase(),
    url: config.url,
    hasToken: !!token,
  });
  
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('Venues API Response:', {
      status: response.status,
      url: response.config.url
    });
    return response;
  },
  (error) => {
    console.error('Venues API Error:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url
    });
    
    if (error.response?.status === 401) {
      console.error('401 Unauthorized - Token may be expired or invalid');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface Venue {
  id: string;
  name: string;
  location: string;
  type: 'event' | 'shooting';
  rate: number;
  rateType: string;
  capacity: number;
  description?: string;
  image?: string;
  status: 'active' | 'inactive' | 'maintenance';
  totalBookings: number;
  amenities?: string[];
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  
  // ✅ NEW: Timing slot properties
  hasTimingSlots?: boolean;
  timingSlotConfig?: TimingSlotConfig;
    termsAndConditions?: string[]; // ✅ ADD THIS LINE

}

export interface VenueFilters {
  type?: 'event' | 'shooting';
  status?: 'active' | 'inactive' | 'maintenance';
  search?: string;
}

export interface CreateVenueData {
  name: string;
  location: string;
  type: 'event' | 'shooting';
  rate: number;
  rateType: string;
  capacity: number;
  description?: string;
  status?: 'active' | 'inactive' | 'maintenance';
  amenities?: string[];
  metadata?: Record<string, any>;
}

export interface UpdateVenueData extends Partial<CreateVenueData> {}

export interface VenueStats {
  total: number;
  active: number;
  inactive: number;
  maintenance: number;
  byType: {
    event: number;
    shooting: number;
  };
}

class VenuesAPI {
  /**
   * Get all venues with optional filters
   */
  async getVenues(filters?: VenueFilters): Promise<Venue[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);

    const response = await api.get(`/venues?${params.toString()}`);
    return response.data;
  }

  /**
   * Get active venues only (PUBLIC endpoint)
   */
  async getActiveVenues(): Promise<Venue[]> {
    const response = await api.get('/venues/active');
    return response.data;
  }

  /**
   * Get available venues for a date range
   */
  async getAvailableVenues(
    startDate: string,
    endDate: string,
    type?: 'event' | 'shooting'
  ): Promise<Venue[]> {
    const params = new URLSearchParams({
      startDate,
      endDate,
    });
    if (type) params.append('type', type);

    const response = await api.get(`/venues/available?${params.toString()}`);
    return response.data;
  }

  /**
   * Get venue by ID
   */
  async getVenue(id: string): Promise<Venue> {
    const response = await api.get(`/venues/${id}`);
    return response.data;
  }

  /**
   * Get venue statistics
   */
  async getVenueStats(): Promise<VenueStats> {
    const response = await api.get('/venues/stats');
    return response.data;
  }

  /**
   * Create venue with image
   */
  async createVenue(data: CreateVenueData, image?: File): Promise<Venue> {
    const formData = new FormData();
    
    formData.append('name', data.name);
    formData.append('location', data.location);
    formData.append('type', data.type);
    formData.append('rate', data.rate.toString());
    formData.append('rateType', data.rateType);
    formData.append('capacity', data.capacity.toString());
    
    if (data.description) {
      formData.append('description', data.description);
    }
    
    if (data.status) {
      formData.append('status', data.status);
    }
    
    if (data.amenities && data.amenities.length > 0) {
      formData.append('amenities', JSON.stringify(data.amenities));
    }
    
    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata));
    }

    if (image) {
      formData.append('image', image);
    }

    const response = await api.post('/venues', formData);
    return response.data;
  }

  /**
   * Update venue with optional image
   */
  async updateVenue(
    id: string,
    data: UpdateVenueData,
    image?: File
  ): Promise<Venue> {
    const formData = new FormData();
    
    if (data.name !== undefined) formData.append('name', data.name);
    if (data.location !== undefined) formData.append('location', data.location);
    if (data.type !== undefined) formData.append('type', data.type);
    if (data.rate !== undefined) formData.append('rate', data.rate.toString());
    if (data.rateType !== undefined) formData.append('rateType', data.rateType);
    if (data.capacity !== undefined) formData.append('capacity', data.capacity.toString());
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.status !== undefined) formData.append('status', data.status);
    
    if (data.amenities) {
      formData.append('amenities', JSON.stringify(data.amenities));
    }
    
    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata));
    }

    if (image) {
      formData.append('image', image);
    }

    const response = await api.patch(`/venues/${id}`, formData);
    return response.data;
  }

  /**
   * Upload/update venue image only
   */
  async uploadVenueImage(id: string, image: File): Promise<Venue> {
    const formData = new FormData();
    formData.append('image', image);

    const response = await api.post(`/venues/${id}/upload-image`, formData);
    return response.data;
  }

  /**
   * Update venue status
   */
  async updateVenueStatus(
    id: string,
    status: 'active' | 'inactive' | 'maintenance'
  ): Promise<Venue> {
    const response = await api.patch(`/venues/${id}/status`, { status });
    return response.data;
  }

  /**
   * Delete venue
   */
  async deleteVenue(id: string): Promise<void> {
    await api.delete(`/venues/${id}`);
  }

  /**
   * Get image URL
   */
  getImageUrl(imagePath?: string): string {
    if (!imagePath) return '';
    
    // If already a full URL, return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    // Get base URL without /api/v1 suffix
    const baseUrl = process.env.REACT_APP_API_URL || 'http://103.108.220.134:8888/api/v1';
    const backendBaseUrl = baseUrl.replace('/api/v1', '');
    
    // Remove leading slash if present to avoid double slashes
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    
    return `${backendBaseUrl}${cleanPath}`;
  }

  /**
   * Helper method to check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  /**
   * Helper method to get current token
   */
  getToken(): string | null {
    return localStorage.getItem('token');
  }


  /**
 * Get available time slots for a specific date
 * @param venueId - Venue ID
 * @param date - Date in YYYY-MM-DD format
 */
async getAvailableTimeSlots(venueId: string, date: string): Promise<string[]> {
  try {
    const response = await api.get(`/venues/${venueId}/available-slots`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch available time slots:', error);
    return []; // Return empty array on error
  }
}

/**
 * Calculate rate for selected time slots
 * @param venueId - Venue ID
 * @param timeSlots - Array of time slots with startTime and endTime
 */
async calculateRate(
  venueId: string, 
  timeSlots: Array<{ startTime: string; endTime: string }>
): Promise<{
  totalHours: number;
  totalAmount: number;
  breakdown: Array<{
    startTime: string;
    endTime: string;
    hours: number;
    rate: number;
  }>;
}> {
  try {
    const response = await api.post(`/venues/${venueId}/calculate-rate`, {
      timeSlots
    });
    return response.data;
  } catch (error) {
    console.error('Failed to calculate rate:', error);
    throw error;
  }
}

/**
 * Check time slot availability for a specific date
 * @param venueId - Venue ID
 * @param date - Date in YYYY-MM-DD format
 * @param timeSlots - Array of time slots to check
 */
async checkTimeSlotAvailability(
  venueId: string,
  date: string,
  timeSlots: Array<{ startTime: string; endTime: string }>
): Promise<{
  available: boolean;
  conflictingBookings: any[];
}> {
  try {
    const response = await api.post(`/venues/${venueId}/check-slot-availability`, {
      date,
      timeSlots
    });
    return response.data;
  } catch (error) {
    console.error('Failed to check time slot availability:', error);
    throw error;
  }
}

/**
 * Get shooting venues with time slot configuration
 */
async getShootingVenuesWithSlots(): Promise<Venue[]> {
  try {
    const response = await api.get('/venues/shooting/with-slots');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch shooting venues with slots:', error);
    throw error;
  }
}
}



export const venuesAPI = new VenuesAPI();
export default venuesAPI;